import type { OrderbookEventHandler, TradesEventHandler } from "@ekidenfi/ts-sdk";
import { useEffect, useRef } from "react";

import { useStores } from "@stores";

import { getEkidenSdkClient, isMockMode } from "../sdk";

const ORDERBOOK_SUBSCRIBE_TIMEOUT_MS = 5000;
const ORDERBOOK_MAX_RETRIES = 5;

export const usePublicSubscriptions = (symbol?: string) => {
  const { orderbookStore, tradesStore } = useStores();
  const retryCountRef = useRef(0);

  useEffect(() => {
    if (!symbol) return;
    const ekidenSdkClient = getEkidenSdkClient();

    orderbookStore.reset();
    tradesStore.clearTrades();
    retryCountRef.current = 0;

    const loadInitialTrades = async () => {
      try {
        const tradesResponse = await ekidenSdkClient.market.getRecentTrades({
          category: "linear",
          symbol,
          limit: 100,
        });
        if (tradesResponse.list && tradesResponse.list.length > 0) {
          const trades = tradesResponse.list.map((trade: (typeof tradesResponse.list)[0]) => ({
            i: trade.exec_id,
            s: trade.symbol,
            S: trade.side,
            v: trade.size,
            p: trade.price,
            T: Number(trade.time),
            seq: trade.seq,
          }));
          tradesStore.processMessage(trades);
        }
      } catch {}
    };

    loadInitialTrades();

    if (isMockMode || !ekidenSdkClient.publicStream) {
      return;
    }

    const orderbookHandler: OrderbookEventHandler = (msg) => {
      try {
        if (msg.op === "event") {
          orderbookStore.processMessage(msg.data);
        }
      } catch {}
    };

    const tradesHandler: TradesEventHandler = (msg) => {
      try {
        if (msg.op === "event") {
          tradesStore.processMessage(msg.data);
        }
      } catch {}
    };

    let unsubOrderbook: (() => void) | undefined;
    let unsubTrades: (() => void) | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let isCancelled = false;

    const subscribeOrderbookWithRetry = () => {
      if (isCancelled) return;

      // Unsubscribe previous attempt if exists
      unsubOrderbook?.();

      try {
        unsubOrderbook = ekidenSdkClient.publicStream!.subscribeOrderbook(symbol, "50", orderbookHandler);
      } catch {
        return;
      }

      // Set timeout to check if subscription succeeded
      timeoutId = setTimeout(() => {
        if (isCancelled) return;

        // Check if orderbook was initialized (received data)
        if (!orderbookStore.initialized) {
          retryCountRef.current += 1;

          if (retryCountRef.current < ORDERBOOK_MAX_RETRIES) {
            // Retry subscription
            subscribeOrderbookWithRetry();
          } else {
            // Max retries reached, set as initialized with empty data
            orderbookStore.setInitializedEmpty();
          }
        }
      }, ORDERBOOK_SUBSCRIBE_TIMEOUT_MS);
    };

    // Start orderbook subscription with retry logic
    subscribeOrderbookWithRetry();

    // Subscribe to trades (no retry needed)
    try {
      unsubTrades = ekidenSdkClient.publicStream.subscribeTrades(symbol, tradesHandler);
    } catch {}

    return () => {
      isCancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      unsubOrderbook?.();
      unsubTrades?.();
    };
  }, [symbol, orderbookStore, tradesStore]);
};
